import styled from "styled-components";
import { useState } from "react";

const SummaryWrapper = styled.div`
    background: linear-gradient(135deg, #ffffff, #f8fafb);
    border-radius: 16px;
    padding: 1.5rem;
    margin-top: 1.5rem;
    box-shadow: 0 4px 12px rgba(26, 46, 64, 0.08);
    border: 1px solid #e1e8ed;

    @media (max-width: 576px) {
      padding: 1rem;
      margin-top: 1.25rem;
    }
  `;

const SummaryTitle = styled.h3`
    font-family: 'Poppins', 'Roboto', sans-serif;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1A2E40;
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `;

const SummaryDemo = ({
  isPremiumPlan,
  nutritionalGoals,
  summaryMacros,
  summaryFocusedMacros,
  advancedGoalSummaryMacros,
  summaryMacroMapping,
  filteredMeals,
  calculateNutrition
}) => {
  const [summaryChartHoverKey, setSummaryChartHoverKey] = useState(null);
  const [summaryChartTooltip, setSummaryChartTooltip] = useState(null);
  const advancedGoalsEnabled =
    isPremiumPlan
    && (
      nutritionalGoals.advancedMacroGoalsEnabled === true
      || nutritionalGoals.advancedMacroGoalsEnabled === 'true'
    );
  const selectedFocusedSummaryMacros = (Array.isArray(summaryMacros) ? summaryMacros : [])
    .filter((macroKey) => summaryFocusedMacros.has(macroKey))
    .filter((macroKey) => advancedGoalsEnabled || !advancedGoalSummaryMacros.has(macroKey));
  const effectiveSummaryMacros = selectedFocusedSummaryMacros;

  if (!effectiveSummaryMacros || effectiveSummaryMacros.length === 0) {
    return (
      <SummaryWrapper>
        <SummaryTitle>Résumé de la journée</SummaryTitle>
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>
          Aucune macro sélectionnée pour le résumé. Configurez vos macros dans les paramètres.
        </div>
      </SummaryWrapper>
    );
  }

  const totals = {};

  Object.values(summaryMacroMapping).forEach(config => {
    totals[config.dataKey] = 0;
  });

  filteredMeals.forEach(meal => {
    meal.foods.forEach(food => {
      const actual = calculateNutrition(food.macronutrients || {}, food.quantity || 100);

      Object.keys(actual).forEach(key => {
        if (!key.endsWith('_100g') && !key.endsWith('_serving') && !key.endsWith('_unit') && !key.endsWith('_value')) {
          totals[key] = (totals[key] || 0) + (actual[key] || 0);
        }
      });
    });
  });

  const directEnergyKcal = Number(totals['energy-kcal'] || totals.energy || totals.calories || 0);
  const chartData = effectiveSummaryMacros
    .map((macroKey) => {
      const macroConfig = summaryMacroMapping[macroKey];
      if (!macroConfig) return null;

      const current = Math.max(0, Number(totals[macroConfig.dataKey] || 0));
      const goalRaw = macroConfig.goalKey ? Number(nutritionalGoals?.[macroConfig.goalKey]) : NaN;
      const hasGoal = Number.isFinite(goalRaw) && goalRaw > 0;
      const normalizedValue = hasGoal ? (current / goalRaw) : current;
      const chartValue = Math.max(normalizedValue, 0.08); // conserve toutes les macros visibles

      return {
        key: macroKey,
        label: String(macroConfig.label || macroKey).replace(/^\w/u, (ch) => ch.toUpperCase()),
        color: macroConfig.color || '#64748b',
        unit: macroConfig.unit || '',
        current,
        goal: hasGoal ? goalRaw : null,
        value: Number.isFinite(chartValue) ? chartValue : 0.08,
      };
    })
    .filter(Boolean);

  const chartTotal = chartData.reduce((sum, macro) => sum + (macro.value || 0), 0);
  const chartDataByKey = new Map(chartData.map((macro) => [macro.key, macro]));

  const formatSummaryLegendValue = (value, unit) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '0';
    }
    if (unit === 'kcal' || unit === 'kJ' || unit === 'mg') {
      return `${Math.round(numericValue)}`;
    }
    return numericValue.toFixed(1);
  };

  const summaryLegendData = effectiveSummaryMacros
    .map((macroKey) => {
      const macroConfig = summaryMacroMapping[macroKey];
      if (!macroConfig) return null;

      const chartMacro = chartDataByKey.get(macroKey) || null;
      const current = totals[macroConfig.dataKey] || 0;
      const percentage = chartMacro && chartTotal > 0
        ? (chartMacro.value / chartTotal) * 100
        : null;

      return {
        key: macroKey,
        label: String(macroConfig.label || macroKey).replace(/^\w/u, (ch) => ch.toUpperCase()),
        unit: macroConfig.unit || '',
        current,
        percentage,
        color: chartMacro?.color || macroConfig.color || '#64748b',
        goal: chartMacro?.goal || null,
      };
    })
    .filter(Boolean);

  const tooltipMacroKey = summaryChartTooltip?.key || summaryChartHoverKey || null;
  const activeTooltipMacro = tooltipMacroKey ? chartDataByKey.get(tooltipMacroKey) : null;
  const activeTooltipPercentage =
    activeTooltipMacro && chartTotal > 0
      ? (activeTooltipMacro.value / chartTotal) * 100
      : 0;

  const renderPieChart = () => {
    if (chartTotal === 0) {
      return (
        <div className="empty-chart">
          <svg viewBox="0 0 24 24" width="48" height="48" style={{ fill: '#94a3b8', opacity: 0.4 }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '0.5rem' }}>Aucune donnée</p>
        </div>
      );
    }

    const size = 280;
    const center = size / 2;
    const radius = size / 2 - 20;
    const innerRadius = radius * 0.56;

    let currentAngle = -90;

    const slices = chartData.map((macro, index) => {
      const value = macro.value || 0;
      const percentage = (value / chartTotal) * 100;
      const angle = (percentage / 100) * 360;

      if (value === 0) return null;

      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      currentAngle = endAngle;

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = center + radius * Math.cos(startRad);
      const y1 = center + radius * Math.sin(startRad);
      const x2 = center + radius * Math.cos(endRad);
      const y2 = center + radius * Math.sin(endRad);

      const largeArc = angle > 180 ? 1 : 0;

      const path = [
        `M ${center} ${center}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      return (
        <g key={macro.key}>
          <path
            className="summary-chart-slice"
            d={path}
            fill={macro.color}
            stroke="white"
            strokeWidth="4"
            onMouseEnter={(event) => {
              setSummaryChartHoverKey(macro.key);
              const svgRect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
              if (svgRect) {
                setSummaryChartTooltip({
                  key: macro.key,
                  x: Math.max(18, Math.min(svgRect.width - 18, event.clientX - svgRect.left)),
                  y: Math.max(18, Math.min(svgRect.height - 18, event.clientY - svgRect.top)),
                });
              }
            }}
            onMouseMove={(event) => {
              const svgRect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
              if (svgRect) {
                setSummaryChartTooltip({
                  key: macro.key,
                  x: Math.max(18, Math.min(svgRect.width - 18, event.clientX - svgRect.left)),
                  y: Math.max(18, Math.min(svgRect.height - 18, event.clientY - svgRect.top)),
                });
              }
            }}
            onMouseLeave={() => {
              setSummaryChartHoverKey(null);
              setSummaryChartTooltip(null);
            }}
            onFocus={() => {
              setSummaryChartHoverKey(macro.key);
              setSummaryChartTooltip({
                key: macro.key,
                x: center,
                y: center - innerRadius - 10,
              });
            }}
            onBlur={() => {
              setSummaryChartHoverKey(null);
              setSummaryChartTooltip(null);
            }}
            tabIndex={0}
            aria-label={`${macro.label} : ${formatSummaryLegendValue(macro.current, macro.unit)} ${macro.unit}`}
            style={{
              cursor: 'pointer',
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))',
              animationDelay: `${index * 220}ms`,
            }}
          />
        </g>
      );
    }).filter(Boolean);

    return (
      <div className="visual-chart-container">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${size} ${size}`}
          style={{ maxWidth: 'min(100%, 340px)', margin: '0 auto', display: 'block' }}
        >
          {slices}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            fill="white"
            stroke="#e1e8ed"
            strokeWidth="4"
            style={{
              filter: 'drop-shadow(0 8px 20px rgba(26, 46, 64, 0.10))'
            }}
          />
          <text
            x={center}
            y={center - 6}
            textAnchor="middle"
            fill="#1A2E40"
            fontSize="28"
            fontWeight="800"
            fontFamily="'Roboto', sans-serif"
          >
            {Math.round(Number.isFinite(directEnergyKcal) ? directEnergyKcal : 0)}
          </text>
          <text
            x={center}
            y={center + 16}
            textAnchor="middle"
            fill="#64748b"
            fontSize="13"
            fontWeight="700"
            fontFamily="'Roboto', sans-serif"
          >
            kcal
          </text>
          <text
            x={center}
            y={center + 34}
            textAnchor="middle"
            fill="#94a3b8"
            fontSize="11"
            fontWeight="600"
            fontFamily="'Roboto', sans-serif"
          >
            Macros sélectionnées
          </text>
        </svg>
        {activeTooltipMacro && (
          <div
            className="chart-hover-tooltip floating"
            role="status"
            aria-live="polite"
            style={{
              left: `${summaryChartTooltip?.x ?? 140}px`,
              top: `${summaryChartTooltip?.y ?? 140}px`,
            }}
          >
            <div className="chart-hover-tooltip-title">{activeTooltipMacro.label}</div>
            <div className="chart-hover-tooltip-meta">
              {`${formatSummaryLegendValue(activeTooltipMacro.current, activeTooltipMacro.unit)} ${activeTooltipMacro.unit}`}
              {Number.isFinite(activeTooltipMacro.goal) ? ` · objectif ${formatSummaryLegendValue(activeTooltipMacro.goal, activeTooltipMacro.unit)} ${activeTooltipMacro.unit}` : ''}
            </div>
            <div className="chart-hover-tooltip-share">
              {activeTooltipPercentage.toFixed(1)}% du graphique
            </div>
          </div>
        )}
        <div className="chart-legend">
          {summaryLegendData.map((macro) => {
            const percentage = Number(macro.percentage || 0);

            return (
              <div key={macro.key} className="chart-legend-item">
                <span className="chart-legend-swatch" style={{ backgroundColor: macro.color }} />
                <div className="chart-legend-main">
                  <span className="chart-legend-label">{macro.label}</span>
                  <span className="chart-legend-meta">
                    {`${formatSummaryLegendValue(macro.current, macro.unit)} ${macro.unit}`}
                    {Number.isFinite(macro.goal) ? ` · objectif ${formatSummaryLegendValue(macro.goal, macro.unit)} ${macro.unit}` : ''}
                  </span>
                </div>
                <span className="chart-legend-share">
                  {percentage >= 10 ? `${Math.round(percentage)}%` : `${percentage.toFixed(1)}%`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <SummaryWrapper>
      <SummaryTitle>Résumé de la journée</SummaryTitle>

      <div className="fusion-summary-content">
        <div className="chart-center-section">
          {renderPieChart()}
        </div>

        <div className="macros-grid">
          {effectiveSummaryMacros.map(macroKey => {
            const macroConfig = summaryMacroMapping[macroKey];

            if (!macroConfig) {
              return null;
            }

            const current = totals[macroConfig.dataKey] || 0;
            const displayLabel = String(macroConfig.label || '').replace(/^\w/u, (ch) => ch.toUpperCase());
            const rawGoal = macroConfig.goalKey ? nutritionalGoals[macroConfig.goalKey] : null;
            const parsedGoal = Number(rawGoal);
            const DEFAULT_GOAL_SUMMARY_MACROS = new Set(['energy-kcal', 'proteins', 'carbohydrates', 'fat']);
            const macroSupportsGoal =
              DEFAULT_GOAL_SUMMARY_MACROS.has(macroKey)
              || (advancedGoalsEnabled && advancedGoalSummaryMacros.has(macroKey));
            const hasGoal =
              macroSupportsGoal
              && Number.isFinite(parsedGoal)
              && parsedGoal > 0;
            const goal = hasGoal ? parsedGoal : null;
            const remaining = hasGoal ? goal - current : null;
            const percentage = hasGoal ? (current / goal) * 100 : 0;

            return (
              <div key={macroKey} className="macro-card-fusion">
                <div className="macro-card-header">
                  <span className="macro-title">{displayLabel}</span>
                </div>

                <div className="macro-values-row">
                  <div className="value-block">
                    <div className="value-label">Total</div>
                    <div className="value-number" style={{ color: macroConfig.color }}>
                      {macroConfig.unit === 'kcal' || macroConfig.unit === 'kJ' ? Math.round(current) : current.toFixed(1)}
                      <span className="value-unit">{macroConfig.unit}</span>
                    </div>
                  </div>

                  {hasGoal && (
                    <>
                      <div className="value-block">
                        <div className="value-label">Objectif</div>
                        <div className="value-number secondary">
                          {macroConfig.unit === 'kcal' || macroConfig.unit === 'kJ' ? Math.round(goal) : goal.toFixed(0)}
                          <span className="value-unit">{macroConfig.unit}</span>
                        </div>
                      </div>

                      <div className="value-block">
                        <div className="value-label">Reste</div>
                        <div
                          className={`value-number ${remaining >= 0 ? 'positive' : 'negative'}`}
                        >
                          {remaining >= 0 ? '+' : ''}{macroConfig.unit === 'kcal' || macroConfig.unit === 'kJ' ? Math.round(remaining) : remaining.toFixed(0)}
                          <span className="value-unit">{macroConfig.unit}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {hasGoal && (
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${Math.min(100, percentage)}%`,
                        backgroundColor: macroConfig.color
                      }}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </SummaryWrapper>
  );
};

export default SummaryDemo;
